import { useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Send, Loader2, ExternalLink, Bot, Bug, FlaskConical, CheckCircle2, XCircle, SearchX, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------- Chat ----------------
const I18N = {
  en: { intro: "Ask an Evony question, or tap a starter below:", copy: "Copy", copied: "Copied", sources: "Sources", searching: "Searching the Evony Guide Wiki…", error: "Error", placeholder: "Ask about Evony…", noReliableSource: "no reliable source", suggestions: [
    { topic: "Generals", q: "What are the best mounted PvP generals?" },
    { topic: "Bosses", q: "What is the power of bosses in Evony?" },
    { topic: "Equipment", q: "What equipment gives the best attack buffs?" },
    { topic: "Research", q: "What does the Academy do and what research is important?" },
    { topic: "Dragons", q: "How do I use dragons in Evony?" } ] },
  fr: { intro: "Posez une question sur Evony ou touchez une suggestion ci-dessous :", copy: "Copier", copied: "Copié", sources: "Sources", searching: "Recherche dans le Wiki Guide Evony…", error: "Erreur", placeholder: "Posez une question sur Evony…", noReliableSource: "aucune source fiable", suggestions: [
    { topic: "Généraux", q: "Quels sont les meilleurs généraux montés en PvP ?" },
    { topic: "Boss", q: "Quelle est la puissance des boss dans Evony ?" },
    { topic: "Équipement", q: "Quel équipement offre les meilleurs bonus d'attaque ?" },
    { topic: "Recherche", q: "À quoi sert l'Académie et quelles recherches sont importantes ?" },
    { topic: "Dragons", q: "Comment utiliser les dragons dans Evony ?" } ] },
  ja: { intro: "Evony について質問するか、下の候補をタップしてください：", copy: "コピー", copied: "コピーしました", sources: "出典", searching: "Evony ガイド Wiki を検索中…", error: "エラー", placeholder: "Evony について質問…", noReliableSource: "信頼できる出典なし", suggestions: [
    { topic: "将軍", q: "PvPで最強の騎兵将軍は？" },
    { topic: "ボス", q: "Evonyのボスのパワーはどれくらい？" },
    { topic: "装備", q: "攻撃バフが最も高い装備は？" },
    { topic: "研究", q: "アカデミーの役割と重要な研究は？" },
    { topic: "ドラゴン", q: "ドラゴンの使い方は？" } ] },
  de: { intro: "Stelle eine Frage zu Evony oder tippe unten einen Vorschlag an:", copy: "Kopieren", copied: "Kopiert", sources: "Quellen", searching: "Durchsuche das Evony-Guide-Wiki…", error: "Fehler", placeholder: "Frage zu Evony stellen…", noReliableSource: "keine zuverlässige Quelle", suggestions: [
    { topic: "Generäle", q: "Was sind die besten berittenen PvP-Generäle?" },
    { topic: "Bosse", q: "Wie stark sind die Bosse in Evony?" },
    { topic: "Ausrüstung", q: "Welche Ausrüstung gibt die besten Angriffsboni?" },
    { topic: "Forschung", q: "Was macht die Akademie und welche Forschung ist wichtig?" },
    { topic: "Drachen", q: "Wie benutze ich Drachen in Evony?" } ] },
  es: { intro: "Haz una pregunta sobre Evony o toca una sugerencia abajo:", copy: "Copiar", copied: "Copiado", sources: "Fuentes", searching: "Buscando en la Wiki Guía de Evony…", error: "Error", placeholder: "Pregunta sobre Evony…", noReliableSource: "sin fuente fiable", suggestions: [
    { topic: "Generales", q: "¿Cuáles son los mejores generales montados para PvP?" },
    { topic: "Jefes", q: "¿Cuánto poder tienen los jefes en Evony?" },
    { topic: "Equipo", q: "¿Qué equipo da los mejores bonos de ataque?" },
    { topic: "Investigación", q: "¿Qué hace la Academia y qué investigación es importante?" },
    { topic: "Dragones", q: "¿Cómo se usan los dragones en Evony?" } ] },
  ko: { intro: "Evony에 대해 질문하거나 아래 추천을 탭하세요:", copy: "복사", copied: "복사됨", sources: "출처", searching: "Evony 가이드 위키 검색 중…", error: "오류", placeholder: "Evony에 대해 질문하세요…", noReliableSource: "신뢰할 수 있는 출처 없음", suggestions: [
    { topic: "장군", q: "PvP 최고의 기병 장군은?" },
    { topic: "보스", q: "Evony 보스의 전투력은 얼마나 되나요?" },
    { topic: "장비", q: "공격 버프가 가장 좋은 장비는?" },
    { topic: "연구", q: "아카데미의 역할과 중요한 연구는?" },
    { topic: "드래곤", q: "드래곤은 어떻게 사용하나요?" } ] },
  ar: { intro: "اطرح سؤالاً عن Evony أو اضغط على اقتراح بالأسفل:", copy: "نسخ", copied: "تم النسخ", sources: "المصادر", searching: "جارٍ البحث في ويكي دليل Evony…", error: "خطأ", placeholder: "اطرح سؤالاً عن Evony…", noReliableSource: "لا يوجد مصدر موثوق", suggestions: [
    { topic: "الجنرالات", q: "ما هي أفضل جنرالات الفرسان في PvP؟" },
    { topic: "الوحوش", q: "ما مقدار قوة الوحوش في Evony؟" },
    { topic: "المعدات", q: "ما المعدات التي تمنح أفضل تعزيزات الهجوم؟" },
    { topic: "البحث", q: "ما دور الأكاديمية وما البحوث المهمة؟" },
    { topic: "التنانين", q: "كيف أستخدم التنانين في Evony؟" } ] },
  pt: { intro: "Faça uma pergunta sobre Evony ou toque em uma sugestão abaixo:", copy: "Copiar", copied: "Copiado", sources: "Fontes", searching: "Pesquisando no Wiki Guia do Evony…", error: "Erro", placeholder: "Faça uma pergunta sobre Evony…", noReliableSource: "sem fonte confiável", suggestions: [
    { topic: "Generais", q: "Quais são os melhores generais montados para PvP?" },
    { topic: "Chefes", q: "Qual é o poder dos chefes no Evony?" },
    { topic: "Equipamento", q: "Qual equipamento dá os melhores bônus de ataque?" },
    { topic: "Pesquisa", q: "O que a Academia faz e qual pesquisa é importante?" },
    { topic: "Dragões", q: "Como usar dragões no Evony?" } ] },
};

function Chat() {
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState("en");
  const [copiedIdx, setCopiedIdx] = useState(null);

  const send = async (preset) => {
    const question = (typeof preset === "string" ? preset : q).trim();
    if (!question || loading) return;
    setQ(""); setError(null); setLoading(true);
    setMsgs((m) => [...m, { role: "user", text: question }]);
    try {
      const { data } = await axios.post(`${API}/rag/query`, { question });
      if (data.detectedLanguage && I18N[data.detectedLanguage]) setLang(data.detectedLanguage);
      setMsgs((m) => [...m, { role: "bot", ...data }]);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const copyAnswer = async (text, i) => {
    const clean = (text || "")
      .replace(/^#{1,6}\s*/gm, "").replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/^\s*[-*]\s+/gm, "• ").replace(/\[(\d+)\]/g, "").trim();
    try { await navigator.clipboard.writeText(clean); } catch (e) { /* ignore */ }
    setCopiedIdx(i); setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1800);
  };

  const t = I18N[lang] || I18N.en;
  const SUGGESTIONS = t.suggestions;

  return (
    <div data-testid="chat-panel" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="ev-chat">
        {msgs.length === 0 && (
          <div data-testid="chat-empty">
            <div className="ev-muted ev-small">{t.intro}</div>
          </div>
        )}
        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} className="ev-msg user" data-testid="chat-user-msg">{m.text}</div>
        ) : (
          <div key={i} className="ev-msg bot" data-testid="chat-bot-msg">
            <div className="ev-msg-head">
              <Bot size={14} /> <span className="ev-badge">{m.detectedLanguage}</span>
              {!m.grounded && <span className="ev-badge ev-badge-amber">{t.noReliableSource}</span>}
              <span className="ev-mono ev-small ev-muted">{m.llm?.model}</span>
            </div>
            {!m.grounded ? (
              <div className="ev-notfound" data-testid="chat-notfound">
                <SearchX size={15} />
                <div className="ev-markdown"><ReactMarkdown>{m.answer}</ReactMarkdown></div>
              </div>
            ) : (
              <div className="ev-answer-wrap">
                <div className="ev-answer ev-markdown" data-testid="chat-answer">
                  <ReactMarkdown>{m.answer}</ReactMarkdown>
                </div>
                <button className="ev-copy" data-testid={`chat-copy-${i}`} title={t.copy}
                  onClick={() => copyAnswer(m.answer, i)}>
                  {copiedIdx === i ? <><Check size={13} /> {t.copied}</> : <><Copy size={13} /> {t.copy}</>}
                </button>
              </div>
            )}
            {m.sources?.length > 0 && (
              <div className="ev-sources">
                <div className="ev-muted ev-small">{t.sources}</div>
                {m.sources.map((s, j) => (
                  <a key={j} className="ev-source" href={s.sourceUrl} target="_blank" rel="noreferrer" data-testid="chat-source">
                    <span className="ev-badge">{s.language}</span> {s.title} <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && <div className="ev-msg bot" data-testid="chat-loading"><Loader2 className="ev-spin" size={16} /> {t.searching}</div>}
        {error && <div className="ev-msg bot ev-badge-amber" data-testid="chat-error">{t.error}: {error}</div>}
      </div>
      <div className="ev-suggestions" data-testid="chat-suggestions" dir={lang === "ar" ? "rtl" : "ltr"}>
        {SUGGESTIONS.map((s, i) => (
          <button key={`${lang}-${i}`} className="ev-chip" data-testid={`chat-suggestion-${i}`}
            onClick={() => send(s.q)} disabled={loading}>
            <span className="ev-chip-topic">{s.topic}</span> {s.q}
          </button>
        ))}
      </div>
      <div className="ev-chat-input" dir={lang === "ar" ? "rtl" : "ltr"}>
        <input data-testid="chat-input" value={q} placeholder={t.placeholder}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button className="ev-send" data-testid="chat-send" onClick={() => send()} disabled={loading}>
          {loading ? <Loader2 className="ev-spin" size={16} /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}

// ---------------- RAG Debug ----------------
function RagDebug() {
  const [q, setQ] = useState("What are the best mounted PvP generals?");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setD(null);
    try { const { data } = await axios.post(`${API}/rag/query`, { question: q }); setD(data); }
    catch (e) { setD({ error: e.message }); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="rag-debug-panel">
      <div className="ev-chat-input">
        <input data-testid="debug-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} />
        <button className="ev-send" data-testid="debug-run" onClick={run} disabled={loading}>
          {loading ? <Loader2 className="ev-spin" size={16} /> : "Trace"}
        </button>
      </div>
      {d && !d.error && (
        <div className="ev-list" style={{ marginTop: 14 }}>
          <div className="ev-card">
            <div className="ev-card-h"><Bug size={15} /> Pipeline</div>
            <div className="ev-kv">
              <div><span className="ev-muted">detected language</span><span><span className="ev-badge">{d.detectedLanguage}</span></span></div>
              <div><span className="ev-muted">fallback chain</span><span className="ev-mono">{d.fallbackChain?.join(" → ")}</span></div>
              <div><span className="ev-muted">grounded</span><span>{String(d.grounded)}</span></div>
              <div><span className="ev-muted">vector backend</span><span className="ev-mono">{d.debug?.vectorBackend}</span></div>
            </div>
            <div className="ev-muted ev-small" style={{ marginTop: 8 }}>Detected entities: {d.debug?.entities?.map((e) => e.name).join(", ") || "none"}</div>
            <div className="ev-muted ev-small">Structured matches: {d.debug?.structuredMatches?.map((m) => m.name).join(", ") || "none"}</div>
          </div>
          <div className="ev-card">
            <div className="ev-card-h">Top candidates (scores)</div>
            <table className="ev-table"><thead><tr><th>Lang</th><th>Title / section</th><th>ent</th><th>lex</th><th>vec</th><th>trust</th><th>final</th></tr></thead>
              <tbody>
                {d.debug?.topCandidates?.slice(0, 8).map((c, i) => (
                  <tr key={i} data-testid="debug-candidate">
                    <td><span className="ev-badge">{c.language}</span></td>
                    <td className="ev-small">{c.title} <span className="ev-muted">/ {c.sectionTitle}</span></td>
                    <td className="ev-mono">{c.scores.entity}</td><td className="ev-mono">{c.scores.lexical}</td>
                    <td className="ev-mono">{c.scores.vector}</td><td className="ev-mono">{c.scores.trust}</td>
                    <td className="ev-mono ev-strong">{c.scores.final}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ev-card">
            <div className="ev-card-h">Selected chunks → context</div>
            {d.debug?.selectedChunks?.map((c, i) => (
              <div key={i} className="ev-chunk" data-testid="debug-selected">
                <span className="ev-badge">{c.language}</span> <span className="ev-badge">{c.translationMethod}</span>
                <span className="ev-mono ev-small"> final {c.scores.final}</span>
                <div className="ev-muted ev-small">{c.preview}</div>
                <a className="ev-link ev-small" href={c.sourceUrl} target="_blank" rel="noreferrer">{c.sourceUrl}</a>
              </div>
            ))}
          </div>
          <div className="ev-card">
            <div className="ev-card-h">Final answer</div>
            <div className="ev-answer">{d.answer}</div>
          </div>
        </div>
      )}
      {d?.error && <div className="ev-card ev-muted">Error: {d.error}</div>}
    </div>
  );
}

// ---------------- Eval ----------------
function Eval() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/rag/eval`); setD(data); }
    finally { setLoading(false); }
  };
  return (
    <div data-testid="eval-panel">
      <button className="ev-send" data-testid="eval-run" onClick={run} disabled={loading} style={{ padding: "10px 18px" }}>
        {loading ? <><Loader2 className="ev-spin" size={16} /> running…</> : <><FlaskConical size={16} /> Run evaluation</>}
      </button>
      {d && (
        <div style={{ marginTop: 14 }}>
          <div className="ev-metrics">
            <div className="ev-metric"><div className="ev-metric-v">{(d.aggregates.languageAccuracy * 100).toFixed(0)}%</div><div className="ev-metric-l">Language</div></div>
            <div className="ev-metric"><div className="ev-metric-v">{(d.aggregates.retrievalAccuracy * 100).toFixed(0)}%</div><div className="ev-metric-l">Retrieval</div></div>
            <div className="ev-metric"><div className="ev-metric-v">{(d.aggregates.unanswerableAccuracy * 100).toFixed(0)}%</div><div className="ev-metric-l">Abstain</div></div>
            <div className="ev-metric"><div className="ev-metric-v">{d.aggregates.total}</div><div className="ev-metric-l">Questions</div></div>
          </div>
          <div className="ev-card" style={{ marginTop: 14 }}>
            <div className="ev-card-h">Results</div>
            <table className="ev-table"><thead><tr><th></th><th>Category</th><th>Question</th><th>Lang</th><th>Expect</th><th>Top</th></tr></thead>
              <tbody>
                {d.results.map((r, i) => (
                  <tr key={i} data-testid="eval-row">
                    <td>{r.pass ? <CheckCircle2 size={15} color="#4fd1a1" /> : <XCircle size={15} color="#f08a8a" />}</td>
                    <td className="ev-mono ev-small">{r.category}</td>
                    <td className="ev-small ev-truncate">{r.question}</td>
                    <td><span className="ev-badge">{r.detectedLanguage}</span>{!r.languageOk && <span className="ev-badge ev-badge-amber">≠{r.expectedLanguage}</span>}</td>
                    <td className="ev-mono ev-small">{r.unanswerable ? "(abstain)" : r.expectSlug}</td>
                    <td className="ev-mono ev-small ev-truncate">{r.topSlugs?.[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RagPanel({ view }) {
  if (view === "chat") return <Chat />;
  if (view === "ragdebug") return <RagDebug />;
  if (view === "eval") return <Eval />;
  return null;
}
